import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatusBadge } from '../../components/admin/StatusBadge';

// Sum of grade_breakdown[g] * grade_pricing[g] across all graded grades
function gradeTotalValue(gradeBreakdown?: Record<string, number>, gradePricing?: Record<string, number>) {
  if (!gradeBreakdown || !gradePricing) return 0;
  return Object.keys(gradeBreakdown).reduce((sum, grade) => {
    const kg = gradeBreakdown[grade];
    const price = gradePricing[grade];
    return sum + (kg && price ? kg * price : 0);
  }, 0);
}

export const CoffeeList: React.FC = () => {
  const [coffees, setCoffees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/coffees?includeArchived=true')
      .then(r => r.json())
      .then(data => { setCoffees(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Delete "${title}"?`)) return;
    await fetch('/api/admin/coffees', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ id }),
    });
    setCoffees(prev => prev.filter(c => c.id !== id));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] tracking-tight">Coffees</h1>
          <p className="text-sm text-[#A3A3A3] mt-1">{coffees.length} products listed</p>
        </div>
        <Link
          to="/admin/coffees/new"
          className="px-5 py-2.5 bg-[#1A1A1A] text-white rounded-xl text-sm font-medium hover:bg-[#2a2a2a] active:scale-[0.98] transition-all shadow-sm shadow-black/10"
        >
          + Add coffee
        </Link>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-white rounded-2xl animate-pulse shadow-sm" />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[#E8E6E2]/60 rounded-2xl overflow-hidden shadow-sm shadow-black/[0.03]">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#E8E6E2]/60 bg-[#FAFAF8]/50">
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Name</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Process</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Score</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Value</th>
                <th className="px-5 py-3.5 text-left text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Status</th>
                <th className="px-5 py-3.5 text-right text-[11px] text-[#A3A3A3] uppercase tracking-[0.12em] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {coffees.map(c => (
                <tr key={c.id} className="border-b border-[#E8E6E2]/60 last:border-0 hover:bg-[#FAFAF8]/50 transition-colors">
                  <td className="px-5 py-4 font-semibold text-[#1A1A1A]">{c.title}</td>
                  <td className="px-5 py-4 text-[#6B6B6B]">{c.processType}</td>
                  <td className="px-5 py-4 text-[#6B6B6B] tabular-nums">{c.cuppingScore}</td>
                  <td className="px-5 py-4 text-[#6B6B6B] tabular-nums">
                    {(() => {
                      const total = gradeTotalValue(c.gradeBreakdown, c.gradePricing);
                      return total > 0
                        ? `$${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                        : `$${c.pricePerKg}/kg`;
                    })()}
                  </td>
                  <td className="px-5 py-4"><StatusBadge status={c.availabilityStatus} /></td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to={`/admin/coffees/${c.id}`} className="px-2.5 py-1.5 text-[12px] font-medium text-[#6B6B6B] hover:text-[#1A1A1A] hover:bg-[#F0EDE8] rounded-lg transition-colors">
                        Edit
                      </Link>
                      <button onClick={() => handleDelete(c.id, c.title)} className="px-2.5 py-1.5 text-[12px] font-medium text-[#C4C4C4] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {coffees.length === 0 && (
            <div className="p-12 text-center">
              <p className="text-[#A3A3A3]">No coffees yet. Add your first product.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
